import { type TFunction } from "i18next"

import type {
  FacetOption,
  GlobalMapFacets,
  GlobalMapFilters
} from "../redux/global-map.types"

interface ActiveFilterChip {
  key: keyof GlobalMapFilters
  label: string
  cleared: Partial<GlobalMapFilters>
}

/**
 * Which `facets` list, if any, resolves a stored id to its label. Mirrors
 * `Leads/.../active-filter-summary.ts`'s `OPTION_SOURCE` — same shape, new
 * field set.
 */
const OPTION_SOURCE: Partial<
  Record<keyof GlobalMapFilters, keyof GlobalMapFacets>
> = {
  salespersonIds: "salesperson",
  projectManagerIds: "projectManager",
  teamMemberIds: "teamMember",
  subcontractorIds: "subcontractor",
  projectStatusIds: "status",
  leadStatusIds: "status",
  projectTypeIds: "projectType",
  region: "region"
}

// Which `FacetOption.entity` a resolved status id must match — status is the
// only facet where the same source list backs two different filter fields
// (see `GlobalMapFilters.projectStatusIds`/`leadStatusIds`'s own comment),
// so `resolveOne` needs to know which entity's column a given key means.
const STATUS_ENTITY: Partial<
  Record<keyof GlobalMapFilters, "project" | "lead">
> = {
  projectStatusIds: "project",
  leadStatusIds: "lead"
}

/** i18n key for the field-name portion of a chip's label — reuses the same
 * keys `GlobalMapFilterBar`'s pickers are labelled with, where one exists. */
const FIELD_LABEL_KEYS: Record<keyof GlobalMapFilters, string> = {
  salespersonIds: "globalMap.filters.salesperson",
  projectManagerIds: "globalMap.filters.projectManager",
  teamMemberIds: "globalMap.filters.teamMember",
  subcontractorIds: "globalMap.filters.subcontractor",
  projectStatusIds: "globalMap.filters.status",
  leadStatusIds: "globalMap.filters.status",
  projectTypeIds: "globalMap.filters.projectType",
  region: "globalMap.filters.region",
  activePeriodStart: "globalMap.filters.activePeriodStart",
  activePeriodEnd: "globalMap.filters.activePeriodEnd",
  nearZip: "globalMap.filters.lookAround",
  nearRadiusMi: "globalMap.filters.lookAround",
  // A flag key (see `FLAG_KEYS` below) — its chip reads as the statement
  // itself ("Future Appointments"), not "Appointment: Future Appointments",
  // so this reuses the exact same label the filter picker's own option uses
  // rather than a separate field-name key.
  hasUpcomingAppointment: "globalMap.filters.futureAppointments",
  // Unused in practice — `includeHistory` is in `NON_CHIP_KEYS` below, so
  // `activeFilterChips` never reaches this entry. Present only because
  // `FIELD_LABEL_KEYS`'s own `Record<keyof GlobalMapFilters, string>` type
  // requires every field to have one.
  includeHistory: "globalMap.filters.history"
}

/** Narrowings that read as a statement rather than a field and a value —
 * mirrors the Leads list's own `FLAG_KEYS` pattern
 * (`Leads/.../active-filter-summary.ts`). */
const FLAG_KEYS: (keyof GlobalMapFilters)[] = ["hasUpcomingAppointment"]

/** `includeHistory` moved out of the filter bar entirely (per explicit
 * product direction) into its own toggle button beside the Lead/Project
 * layer pills (`HistoryToggleButton.tsx`) — the same "lives in this app's
 * chrome, not the filter row" treatment `activeLayers`/`isLayersNarrowed`
 * already get. It still lives inside `GlobalMapFilters` (so `clearFilters()`
 * naturally resets it to `false` alongside every other field, and
 * `hasAnyActiveFilter` below still counts it toward enabling "Clear All",
 * mirroring how a narrowed layer toggle does the same with no chip of its
 * own) — this set is what keeps it out of the rendered chip row specifically. */
const NON_CHIP_KEYS = new Set<keyof GlobalMapFilters>(["includeHistory"])

/** Chip order — mirrors the field order `GlobalMapFilterBar` renders its
 * pickers in, so the chip row does not reshuffle as filters come and go.
 *
 * A plain `(keyof GlobalMapFilters)[]` annotation only checks that each
 * listed key IS one of `GlobalMapFilters`' fields — it can't catch a field
 * missing from the list, so a future new filter could go silently un-chipped
 * (Finding 7, Increment 3 final whole-branch review). Declaring the order as
 * an object satisfying `Record<keyof GlobalMapFilters, true>` instead — the
 * same `satisfies`-based gate `FIELD_LABEL_KEYS` above and
 * `useGlobalMapUrlSync.ts`'s own `FILTER_PARAMS` already use — makes a
 * missing (or misspelled) field a compile error; `Object.keys` then recovers
 * the plain ordered array every caller below actually wants, since object
 * literal key order is preserved for string keys. */
const GLOBAL_MAP_FILTER_KEY_ORDER = {
  salespersonIds: true,
  projectManagerIds: true,
  teamMemberIds: true,
  subcontractorIds: true,
  activePeriodStart: true,
  activePeriodEnd: true,
  projectStatusIds: true,
  leadStatusIds: true,
  projectTypeIds: true,
  region: true,
  nearZip: true,
  nearRadiusMi: true,
  hasUpcomingAppointment: true,
  includeHistory: true
} as const satisfies Record<keyof GlobalMapFilters, true>

const GLOBAL_MAP_FILTER_KEYS = Object.keys(
  GLOBAL_MAP_FILTER_KEY_ORDER
) as (keyof GlobalMapFilters)[]

// Widened from two array fields to six — the four relationship filters are
// now multi-select too, so their unset shape is `[]`, same as
// `projectStatusIds`/`leadStatusIds`/`projectTypeIds`.
const ARRAY_FILTER_KEYS = new Set<keyof GlobalMapFilters>([
  "salespersonIds",
  "projectManagerIds",
  "teamMemberIds",
  "subcontractorIds",
  "projectStatusIds",
  "leadStatusIds",
  "projectTypeIds",
  "region"
])
const NULLABLE_FILTER_KEYS = new Set<keyof GlobalMapFilters>([
  "activePeriodStart",
  "activePeriodEnd",
  "nearZip",
  "nearRadiusMi"
])

/**
 * A filter is narrowing only when it holds something — an empty array
 * (the two multi-selects' unset shape) or `""`/`null`/`undefined` (every
 * other field's unset shape) render no chip. Identical to the Leads
 * pattern's `isNarrowing`.
 */
const isNarrowing = (value: unknown) => {
  if (value === null || value === undefined || value === "") return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === "boolean") return value
  return true
}

/** What `setFilters` should receive to clear one field, matching
 * `GlobalMapFilterBar`'s own clear conventions for each field's shape:
 * `[]` for the two array filters, `null` for the two date bounds (same
 * "clearing writes an explicit null" convention `GlobalMapFilters` itself
 * documents), `undefined` for everything else. */
const clearedValueFor = (
  key: keyof GlobalMapFilters
): [] | null | undefined => {
  if (ARRAY_FILTER_KEYS.has(key)) return []
  if (NULLABLE_FILTER_KEYS.has(key)) return null
  return undefined
}

/**
 * Resolves one stored value to its display label. Facets may not be loaded
 * yet — `fetchFacets` isn't dispatched anywhere as of Task 16 — so every
 * facet-backed lookup here falls back to the raw stored value rather than
 * crashing or rendering "undefined".
 */
const resolveOne = (
  key: keyof GlobalMapFilters,
  entry: unknown,
  facets?: GlobalMapFacets | null
): string => {
  const source = OPTION_SOURCE[key]
  const options: FacetOption[] | undefined = source
    ? facets?.[source]
    : undefined
  // The status facet returns BOTH project and lead options, some sharing a
  // numeric value (see `STATUS_ENTITY`'s own comment) — a plain
  // value-only match would resolve `projectStatusIds`'s stored id against
  // whichever entity's option happens to come first, potentially printing
  // the wrong label. Every other facet has no `entity` on its options, so
  // this predicate degrades to the old value-only match for them.
  const entity = STATUS_ENTITY[key]
  const match = options?.find(
    (option) =>
      String(option.value) === String(entry) &&
      (!entity || option.entity === entity)
  )

  return match?.label != null ? String(match.label) : String(entry)
}

const describeValue = (
  key: keyof GlobalMapFilters,
  value: unknown,
  facets?: GlobalMapFacets | null
): string =>
  (Array.isArray(value) ? value : [value])
    .map((entry) => resolveOne(key, entry, facets))
    .join(", ")

/**
 * One chip per narrowing currently applied, in `GLOBAL_MAP_FILTER_KEYS`
 * order. Mirrors `Leads/.../active-filter-summary.ts`'s `activeFilterChips`
 * — same shape, new field set, no `FLAG_KEYS` branch since no
 * `GlobalMapFilters` field reads as a bare statement.
 */
const activeFilterChips = (
  filters: GlobalMapFilters,
  t: TFunction,
  facets?: GlobalMapFacets | null
): ActiveFilterChip[] =>
  GLOBAL_MAP_FILTER_KEYS.filter(
    (key) => isNarrowing(filters[key]) && !NON_CHIP_KEYS.has(key)
  ).map((key) => ({
    key,
    label: FLAG_KEYS.includes(key)
      ? t(FIELD_LABEL_KEYS[key])
      : `${t(FIELD_LABEL_KEYS[key])}: ${describeValue(key, filters[key], facets)}`,
    cleared: { [key]: clearedValueFor(key) } as Partial<GlobalMapFilters>
  }))

/**
 * Whether any `GlobalMapFilters` field currently narrows the map — used to
 * enable/disable the standalone "Clear All" button in `GlobalMapFilterBar`
 * (the per-field chip row this once lived beside is gone; see that
 * component's own docstring). Reuses `isNarrowing`/`GLOBAL_MAP_FILTER_KEYS`
 * directly rather than `activeFilterChips(filters, t).length > 0`, which
 * would needlessly require a `t` and resolve every chip's display label
 * just to answer a boolean.
 */
const hasAnyActiveFilter = (filters: GlobalMapFilters): boolean =>
  GLOBAL_MAP_FILTER_KEYS.some((key) => isNarrowing(filters[key]))

export {
  type ActiveFilterChip,
  GLOBAL_MAP_FILTER_KEYS,
  activeFilterChips,
  hasAnyActiveFilter
}
